/**
 * FacesLoaderDynamic - Chargement dynamique des parts multi-produits
 * 
 * Ce composant est aligné sur FacesLoader du backoffice.
 * Il charge les parts via l'API backend et les positionne via le système d'ancrage mondial v2.0.
 */

import { useEffect, useRef, useState, useMemo } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { getMaterialFromFormData } from "../../helpers/getMaterialFromFormData";
import { applyMapRotation, applyTextureTileRepeat, getMaterialTextureTileMm, syncNormalMapFromColorMap } from "../../helpers/materialTexture";
import PlaceholderCube, { evaluateDisplayFormula } from "./PlaceholderCube";
import {
  buildProductSelectionDebug,
  fetchPublicProducts,
  fetchProductConfig,
  selectProductLocally,
} from "../../helpers/ProductsApi";
import { isProductSelectionDebugAllowed } from "../../helpers/productSelectionDebug";
import {
  resolvePositions,
  computeBBoxExcludingCutoutFlaps,
} from "../../helpers/anchorSystem";
import {
  addMeshEdgeLines,
  createPartSeamLine,
  getPartDims,
} from "../../helpers/edgeOverlay";
import { fetchMessageRules } from "../../helpers/MessageRulesApi";
import { resolveDecValues } from "../../helpers/decHelpers";
import {
  DEFAULT_INSTALL_TYPE,
  PLACEHOLDERS_GLOBAL_KEY,
  PLACEHOLDERS_LEGACY_KEY,
  normalizePlaceholdersFlat,
} from "../../helpers/installTypes";

const OTS_URL = process.env.REACT_APP_OTS_URL;
const FACE_SCALE = 0.1;

/**
 * Génère une clé de cache basée sur les dimensions qui affectent cette part
 * ALIGNÉ SUR LE BACKOFFICE - inclut les dimensions de référence et le mode resize
 * 
 * ⚠️ segDims est en convention FRONTEND (sizeX=largeur, sizeY=hauteur, sizeZ=profondeur)
 * ⚠️ refSizes est en convention BACKEND (x=profondeur, y=largeur, z=hauteur)
 * ⚠️ Les axes (resizeAxisX/Y) font référence aux axes BACKEND
 */
function getCacheKey(part, segDims, patternId, refSizes = null, textureTileMm = null) {
  const partId = part.clientId || part.id;
  const axisX = (part.resizeAxisX ?? 'y').replace('-', '');
  const axisY = (part.resizeAxisY ?? 'z').replace('-', '');
  
  // Convertir segDims de frontend vers backend pour la clé de cache
  // Frontend: sizeX=largeur, sizeY=hauteur, sizeZ=profondeur
  // Backend: X=profondeur, Y=largeur, Z=hauteur
  const backendDims = {
    X: segDims.sizeZ,  // frontend Z (profondeur) → backend X
    Y: segDims.sizeX,  // frontend X (largeur) → backend Y
    Z: segDims.sizeY,  // frontend Y (hauteur) → backend Z
  };
  
  const dimX = backendDims[axisX.toUpperCase()] || backendDims.Y;
  const dimY = backendDims[axisY.toUpperCase()] || backendDims.Z;
  
  const zoneConfig = part.zoneConfig;
  const hasZones = zoneConfig?.zones?.length > 0;
  const zonesKey = hasZones 
    ? `z${zoneConfig.lines?.length ?? 0}_${zoneConfig.zones?.length ?? 0}`
    : 'z0';
  
  // Déterminer si les dimensions ont changé (comparer backend avec backend)
  const dimensionsChanged = refSizes && (
    (refSizes.x && Math.abs(backendDims.X - refSizes.x) > 1) ||
    (refSizes.y && Math.abs(backendDims.Y - refSizes.y) > 1) ||
    (refSizes.z && Math.abs(backendDims.Z - refSizes.z) > 1)
  );
  const activeMode = (!dimensionsChanged) ? 'native' : 'resized';
  
  // Inclure les dimensions de référence dans la clé (backend)
  const refKey = refSizes 
    ? `r${Math.round(refSizes.x || 0)}_${Math.round(refSizes.y || 0)}_${Math.round(refSizes.z || 0)}`
    : 'r0';
  
  const tileKey = textureTileMm ? `_t${Math.round(textureTileMm)}_l` : '';
  return `${partId}_${axisX}${Math.round(dimX)}_${axisY}${Math.round(dimY)}_p${patternId ?? 'none'}_${activeMode}_${zonesKey}_${refKey}${tileKey}`;
}

/**
 * Appelle l'OTS pour générer le modèle 3D d'un part
 * NOTE: Le DXF n'est PAS envoyé par le frontend (fichier sensible)
 * L'OTS récupère le DXF directement depuis le backend (serveur à serveur)
 * 
 * @param {number} productId - ID du produit
 * @param {object} part - Configuration de la part
 * @param {object} segmentedDims - Dimensions demandées (après segmentation)
 * @param {number} patternId - ID du motif
 * @param {object} refSizes - Dimensions de référence du produit { x, y, z }
 */
const OTS_TIMEOUT_MS = 60000;
// Retries OTS sur fetch transitoire (timeout, 5xx, abort). 2 retries = 3
// tentatives au total. Backoff exponentiel : 1500ms puis 3000ms.
const OTS_MAX_RETRIES = 2;
const OTS_RETRY_DELAY_MS = 1500;
// Délai avant un retry "effect-level" pour les parts toujours en échec après
// les retries inline. Permet de re-déclencher le useEffect principal pour
// retenter le chargement sans attendre un changement de dimensions client.
const OTS_EFFECT_RETRY_DELAY_MS = 5000;

const fetchPartGltfOnce = async (productId, part, segmentedDims, patternId, materialTextureSize) => {
  // THIN-CLIENT (Phase 2) : le renderer n'envoie QUE l'identité + les
  // dimensions cibles + le motif. Toute la géométrie (folds, zones, axes,
  // zoneConfig, refSize, useNativeDimensions, mainContourColor, continuité
  // motif, placements patron) est résolue côté OTS depuis la config backend.
  //
  // ⚠️ CONVENTION DIMENSIONS : on envoie en convention BACKEND
  //    (X=profondeur, Y=largeur, Z=hauteur), seule convention acceptée par l'OTS.
  const backendSizeX = segmentedDims.sizeZ;  // frontend Z (profondeur) → backend X
  const backendSizeY = segmentedDims.sizeX;  // frontend X (largeur)    → backend Y
  const backendSizeZ = segmentedDims.sizeY;  // frontend Y (hauteur)    → backend Z

  const meta = {
    productId,
    partId: part.id,
    name: part.name,
    sizeX: backendSizeX,
    sizeY: backendSizeY,
    sizeZ: backendSizeZ,
    patternId: patternId || null,
    ...(materialTextureSize ? { materialTextureSize } : {}),
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), OTS_TIMEOUT_MS);

  try {
    const response = await fetch(`${OTS_URL}/process/obj-face`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(meta),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`OTS ${response.status}: ${errorText}`);
    }

    const buffer = await response.arrayBuffer();
    const loader = new GLTFLoader();

    return new Promise((resolve, reject) => {
      loader.parse(buffer, '', resolve, reject);
    });
  } finally {
    clearTimeout(timeoutId);
  }
};

const fetchPartGltf = async (productId, part, segmentedDims, patternId, materialTextureSize) => {
  for (let attempt = 0; attempt <= OTS_MAX_RETRIES; attempt++) {
    try {
      return await fetchPartGltfOnce(productId, part, segmentedDims, patternId, materialTextureSize);
    } catch (error) {
      const isLastAttempt = attempt === OTS_MAX_RETRIES;
      if (isLastAttempt) {
        console.error(`[FacesLoaderDynamic] "${part.name}" failed after ${attempt + 1} attempt(s):`, error);
        return null;
      }
      // Backoff exponentiel : 1500ms × 2^attempt → 1500ms, 3000ms, 6000ms...
      const delay = OTS_RETRY_DELAY_MS * Math.pow(2, attempt);
      console.warn(`[FacesLoaderDynamic] "${part.name}" attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
      await new Promise(r => setTimeout(r, delay));
    }
  }
  return null;
};

const FacesLoaderDynamic = ({
  modelFormData,
  materialFormData,
  hide3D,
  setIsLoading,
  onAllFacesLoaded,
  allTextures,
  isMobile,
  globalSceneSettings,
  onProductConfigLoaded,
  // 🆕 #1961 — remonte l'ECHEC de selection au parent. Jusqu'ici un echec ne
  // produisait qu'un `console.warn` : il etait indistinguable d'un succes pour
  // tout le reste de l'application, et le panier partait avec le produit
  // precedent (ou le defaut arme au chargement de la page). Recoit `true` quand
  // aucun produit ne matche, `false` des qu'une selection aboutit.
  onProductSelectionFailed,
  onSelectionDebugChange,
}) => {
  // États
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productConfig, setProductConfig] = useState(null);
  const [meshes, setMeshes] = useState([]); // Array de { mesh, partInfo } COMME LE BACKOFFICE
  const [placeholderSize, setPlaceholderSize] = useState(null);
  const [meshGeneration, setMeshGeneration] = useState(0); // Force re-render des primitives
  // Snapshot "publié" de la sélection produit : { parts, hasProduct }.
  // Le rendu (meshes produit + placeholders) ne lit QUE ce snapshot, qui n'est
  // committé qu'une fois le chargement entièrement terminé (ou la sélection
  // résolue en "aucun produit"). Tant qu'il vaut null, on n'affiche rien.
  // Pendant un changement de dimensions, le snapshot précédent reste affiché
  // (figé, grisé via ThreeScene) le temps que la nouvelle config se charge,
  // ce qui évite les états intermédiaires incohérents (placeholders ancrés sur
  // d'anciens meshes, bascule produit ⇄ pas-de-produit transitoire, etc.).
  const [committedProduct, setCommittedProduct] = useState(null);
  // Snapshot "publié" des dimensions d'installation (L/H/P1/P2/DEC/INSTALL)
  // utilisées par les formules des placeholders. Comme les meshes, il n'est mis
  // à jour qu'au moment du commit (chargement terminé) — sinon les placeholders
  // sauteraient à leurs nouvelles positions/visibilités AVANT que la nouvelle
  // config 3D ne soit prête, alors que la scène précédente est encore affichée
  // (figée/grisée). On veut que placeholders ET meshes apparaissent ensemble.
  const [committedInstallDims, setCommittedInstallDims] = useState(null);
  // selectionKey du dernier commit. Sert de garde FIABLE (basée sur les valeurs,
  // pas sur le timing des effets ni sur hide3D) pour l'effet de synchro "à vide"
  // des placeholders : si le selectionKey courant DIFFÈRE du committé, un
  // rechargement (produit/dimensions/type/motif) est en cours → on gèle les
  // installDims jusqu'au commit. S'il est ÉGAL, aucun reload n'est nécessaire
  // (ex. cache verrouillé : L/H/P1/P2 changent sans toucher aux dimensions du
  // cache) → on peut rafraîchir les installDims en direct.
  const [committedSelectionKey, setCommittedSelectionKey] = useState(null);
  // Placeholders globaux (liste unique, conditionnels via INSTALL dans les
  // formules). Stockes dans GlobalSettings.placeholders_global. Charges une
  // seule fois (cache MessageRulesApi). Remplacent les placeholders du produit
  // (productConfig.placeholders) qui restent en DB mais dormants.
  // Fallback rétro-compatible sur GlobalSettings.placeholders_by_install
  // (ancien format objet par install, dédupliqué par id au chargement).
  const [globalPlaceholders, setGlobalPlaceholders] = useState([]);
  
  // Refs - CACHE PAR REF COMME LE BACKOFFICE
  const meshCacheRef = useRef(new Map());
  const prevDimensionsRef = useRef();
  const loadingRef = useRef(false);
  // Retry "effect-level" : compteur d'état incrémenté par un setTimeout
  // déclenché quand certaines parts sont en échec après les retries inline.
  // Re-trigger le useEffect principal sans attendre un changement de
  // dimensions client. Cf. C.2 du plan.
  const [retryTick, setRetryTick] = useState(0);
  const pendingRetryRef = useRef(null);
  const [materialRevision, setMaterialRevision] = useState(0);
  const baseMatRef = useRef({ key: null, mat: null });

  // Résolution du matériau sélectionné
  const resolvedMaterial = useMemo(() => {
    if (!materialFormData?.material) return null;
    if (materialFormData?.color) return materialFormData.color;
    return allTextures?.find((mat) => String(mat.id) === String(materialFormData.material));
  }, [materialFormData, allTextures]);

  // 1. Charger la liste des produits au montage
  useEffect(() => {
    const loadProducts = async () => {
      const fetchedProducts = await fetchPublicProducts();
      setProducts(fetchedProducts);
    };
    loadProducts();
  }, []);

  // Charger les placeholders globaux au montage via /message-rules/public
  // (qui expose tous les GlobalSettings sans auth, deja en cache).
  // Priorité nouvelle clé `placeholders_global` (liste plate), fallback sur
  // l'ancienne `placeholders_by_install` (objet par install, on dédup par id).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { settings } = await fetchMessageRules();
        if (cancelled) return;
        const raw = settings?.[PLACEHOLDERS_GLOBAL_KEY]
          ?? settings?.[PLACEHOLDERS_LEGACY_KEY];
        if (raw != null) {
          setGlobalPlaceholders(normalizePlaceholdersFlat(raw));
        }
      } catch (e) {
        console.warn('[FacesLoaderDynamic] Erreur chargement placeholders globaux:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Dimensions mappées
  // ⚠️ MAPPING AXES: Les règles displayExpression utilisent la convention backend:
  //    Backend: X=profondeur, Y=largeur, Z=hauteur (AVANT, affichée sur le cache)
  //    Renderer: sizeX=largeur, sizeZ=profondeur, frontHeight=hauteur AVANT, sizeY=hauteur ARRIÈRE
  const mappedDimensions = useMemo(() => {
    const largeur = Number(modelFormData.sizeX) || 890;
    const hauteurAvant = Number(modelFormData.frontHeight) || 667; // ✅ Utiliser hauteur AVANT pour sélection produit
    const profondeur = Number(modelFormData.sizeZ) || 480;
    
    return {
      X: largeur,
      Y: hauteurAvant, // ✅ Utiliser hauteur AVANT pour sélection produit
      Z: profondeur,
      backendX: profondeur,
      backendY: largeur,
      backendZ: hauteurAvant, // ✅ Utiliser hauteur AVANT pour sélection produit (convention backend Z=hauteur AVANT)
    };
  }, [modelFormData.sizeX, modelFormData.frontHeight, modelFormData.sizeZ]);

  // Dimensions d'installation pour les formules des placeholders
  // L, H, P1, P2 sont en mm (comme les dimensions du cache).
  // DEC : on privilegie productConfig.dec (champ admin), fallback derive du type.
  // Mapping nouveaux IDs (renommage 25/05/2026) :
  //   'mur'     = !WALLMOUNT                 -> DEC 10  (ex 'encastrement')
  //   'sol'     = !WALLMOUNT                 -> DEC 10
  //   'sol-mur' = WALLMOUNT && !BOTTOM       -> DEC 0   (ex 'facade-sol')
  //   'mur-haut' = WALLMOUNT &&  BOTTOM      -> DEC 40  (ex 'facade-haut')
  const installDims = useMemo(() => {
    const L = Number(modelFormData.sizeL) || 0;
    const H = Number(modelFormData.sizeH) || 0;
    const P1 = Number(modelFormData.sizeP1) || 0;
    const P2 = Number(modelFormData.sizeP2) || 0;

    // DEC est désormais scindé : DECi (install) + DECc (produit). DEC reste
    // alias strict de DECi pour rétrocompat des formules existantes. Les
    // formules qui veulent l'ancien fallback "produit sinon install" doivent
    // utiliser explicitement `DECc ?? DECi`.
    const installType = modelFormData.type;
    const { DEC, DECi, DECc } = resolveDecValues(installType, productConfig?.dec);

    // INSTALL = choix client (type d'installation, etape 3). Disponible dans
    // toutes les formules.
    const INSTALL = modelFormData.type || DEFAULT_INSTALL_TYPE;

    return { L, H, P1, P2, DEC, DECi, DECc, INSTALL };
  }, [modelFormData.sizeL, modelFormData.sizeH, modelFormData.sizeP1, modelFormData.sizeP2, modelFormData.type, productConfig?.dec]);

  // Placeholders effectivement rendus : liste unique de placeholders globaux.
  // Les variations selon INSTALL sont encodees DANS LES FORMULES via des
  // conditionnels (ex: `INSTALL === 'mur-haut' ? 40 : 0`).
  const effectivePlaceholders = globalPlaceholders;

  // Options configurateur pour les regles d'affichage
  // Aligne sur l'evaluateur du backoffice (products/page.jsx -> evaluateExpression).
  // WALLMOUNT provient désormais de `modelFormData.wallmount` (état indépendant),
  // plus de la dérivation `type === 'sol-mur' || type === 'mur-haut'`. Cela
  // permet à un client en `sol-mur` (installation au sol contre un mur) de
  // choisir librement entre cache mural et cache au sol — choix débrayé sur le
  // toggle de TypeForm. BOTTOM reste piloté par l'utilisateur (case "face du
  // dessous" en `mur-haut`). DEC reste dérivé du type d'installation seul.
  // HTOP reste false côté client. REAR est piloté par modelFormData.rear (face
  // arrière, option `sol` — pas encore activable dans TypeForm).
  const displayOptions = useMemo(() => {
    const type = modelFormData.type || '';
    const wallmount = !!modelFormData.wallmount;
    const bottom = !!modelFormData.bottom;
    const rear = !!modelFormData.rear;
    // DEC reste alias strict de DECi (dérivé du type d'install) pour
    // rétrocompat des règles d'affichage. DECc (Products.dec) est exposé en
    // sus pour permettre la sélection produit basée sur le décalage cache.
    // Les anciennes règles qui voulaient `productDec ?? installDerived` doivent
    // être ré-éditées dans le BO en `DECc ?? DECi`.
    const { DEC, DECi, DECc } = resolveDecValues(type, productConfig?.dec);
    return {
      BOTTOM: bottom,
      HTOP: false,
      WALLMOUNT: wallmount,
      REAR: rear,
      TYPE: type,
      INSTALL: type,
      DEC,
      DECi,
      DECc,
    };
  }, [modelFormData.type, modelFormData.wallmount, modelFormData.bottom, modelFormData.rear, productConfig?.dec]);

  // Clé de sélection : dimensions cache + options d'installation (changement de
  // type sol-mur → sol, etc. sans variation de sizeX peut changer le produit).
  const selectionKey = useMemo(() => {
    const { backendX, backendY, backendZ } = mappedDimensions;
    const o = displayOptions;
    return [
      backendX,
      backendY,
      backendZ,
      modelFormData.pattern ?? "",
      o.TYPE,
      o.WALLMOUNT ? 1 : 0,
      o.BOTTOM ? 1 : 0,
      o.REAR ? 1 : 0,
    ].join("|");
  }, [mappedDimensions, displayOptions, modelFormData.pattern]);

  const lastSelectedProductIdRef = useRef(null);

  // Pause (spinner + scène grisée) dès qu'un nouveau produit / config est en cours
  // de résolution — pas seulement au changement de motif.
  useEffect(() => {
    if (hide3D || products.length === 0) return;
    const { backendX, backendY, backendZ } = mappedDimensions;
    const selected = selectProductLocally(
      products,
      backendX,
      backendY,
      backendZ,
      displayOptions,
    );
    if (!selected) {
      setIsLoading(false);
      return;
    }
    prevDimensionsRef.current = null;
    setIsLoading(true);
  }, [selectionKey, hide3D, products.length, mappedDimensions, displayOptions, setIsLoading]);

  // 2. Sélectionner le produit selon les dimensions + options
  useEffect(() => {
    if (products.length === 0) return;

    const { backendX, backendY, backendZ } = mappedDimensions;
    const selected = selectProductLocally(products, backendX, backendY, backendZ, displayOptions);
    
    if (selected) {
      if (lastSelectedProductIdRef.current !== selected.id) {
        lastSelectedProductIdRef.current = selected.id;
        setProductConfig(null);
      }
      setSelectedProduct(selected);
      onProductSelectionFailed?.(false);
    } else {
      lastSelectedProductIdRef.current = null;
      console.warn('[FacesLoaderDynamic] No product matches dimensions');
      setSelectedProduct(null);
      setProductConfig(null);
      // 🆕 #1961 — le parent DOIT le savoir : `onProductConfigLoaded` n'est pas
      // rappele sur un echec, donc `selectedProductId` cote Configurator reste
      // sur sa valeur precedente. Sans ce signal, le panier part avec elle.
      onProductSelectionFailed?.(true);
    }
  }, [products, mappedDimensions, displayOptions, onProductSelectionFailed]);

  // 3. Charger la configuration du produit sélectionné (une fois par productId)
  const loadedConfigProductIdRef = useRef(null);

  useEffect(() => {
    const productId = selectedProduct?.id;

    if (!productId) {
      loadedConfigProductIdRef.current = null;
      setProductConfig(null);
      return;
    }

    if (loadedConfigProductIdRef.current === productId) {
      return;
    }

    let cancelled = false;

    const loadConfig = async () => {
      // 🆕 #1961 — `fetchProductConfig` avale toute erreur et renvoie `null`.
      // Une seule tentative rendait l'echec DEFINITIF : la dependance de cet
      // effet est `[selectedProduct?.id]`, elle ne change pas, l'effet ne se
      // rejoue jamais. On retente brievement avant d'abandonner.
      let config = null;
      for (let tentative = 0; tentative < 3; tentative += 1) {
        if (cancelled) return;
        if (tentative > 0) {
          await new Promise((resoudre) => setTimeout(resoudre, 400 * tentative));
          if (cancelled) return;
        }
        config = await fetchProductConfig(productId);
        if (config) break;
      }
      if (cancelled) return;

      if (!config) {
        // 🆕 #1961 — la selection a ABOUTI mais sa config n'a pas pu etre
        // chargee. `selectedProductId` cote Configurator n'est pose QUE dans
        // `onProductConfigLoaded` : il reste donc sur sa valeur PRECEDENTE (le
        // defaut au premier chargement, ou le produit d'avant), et rien ne le
        // signale — `productSelectionFailed` ne couvrait que l'echec de
        // SELECTION. Sans cette ligne, le panier part avec ce produit perime.
        // Echec ferme.
        console.error(
          `[FacesLoaderDynamic] #1961 — config du produit ${productId} illisible `
            + `apres 3 tentatives : selection consideree comme non aboutie`,
        );
        onProductSelectionFailed?.(true);
        return;
      }

      loadedConfigProductIdRef.current = productId;
      setProductConfig(config);
      onProductConfigLoaded?.(config);
    };

    loadConfig();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.id]);

  // Q5 (audit) : aligne sur le backoffice -> toutes les parts `enabled` sont
  // affichees, le flag `optional` n'est plus utilise comme filtre c\u00f4t\u00e9 rendu.
  // Si une part doit \u00eatre conditionnelle, il faut passer par une display rule
  // c\u00f4t\u00e9 produit (et selectionner un autre produit pour la cacher).

  // Dimensions de référence du produit (convention BACKEND: X=prof, Y=larg, Z=haut)
  // Dimensions de référence produit (DB) — même source que pattern-patron-placements backend
  const refSizes = useMemo(() => {
    if (productConfig?.sizeX != null) {
      return {
        x: Number(productConfig.sizeX),
        y: Number(productConfig.sizeY),
        z: Number(productConfig.sizeZ),
      };
    }
    if (!selectedProduct) return null;
    return {
      x: selectedProduct.sizeX,
      y: selectedProduct.sizeY,
      z: selectedProduct.sizeZ,
    };
  }, [productConfig?.sizeX, productConfig?.sizeY, productConfig?.sizeZ, selectedProduct]);

  // Éviter d'afficher des meshes d'un autre produit pendant un changement.
  useEffect(() => {
    meshCacheRef.current.clear();
  }, [productConfig?.id]);

  // Pré-calculer les dimensions pour chaque part
  const partsWithSegDims = useMemo(() => {
    if (!productConfig || !productConfig.parts) {
      return [];
    }
    
    const { X, Y, Z } = mappedDimensions;
    
    // Q5 (audit) : aligné sur le backoffice. Cf. preview.jsx — on filtre :
    //   - `enabled !== false`     (kill-switch global, exclu en 3D + DXF)
    //   - `include_3d !== false`  (inclusion dans le pipeline 3D uniquement,
    //                              décochée par exemple pour un gabarit de
    //                              perçage qui n'a pas besoin d'être rendu)
    // Le backend `/products/:id/config` filtre déjà ces deux conditions, mais
    // on garde le filtre client pour rester défensif.
    const filteredParts = productConfig.parts.filter(
      p => p.enabled !== false && p.include_3d !== false
    );

    const pattern = modelFormData.pattern;
    const textureTileMm = getMaterialTextureTileMm(resolvedMaterial);

    return filteredParts.map(part => {
      const segDims = { sizeX: X, sizeY: Y, sizeZ: Z };
      // La continuité patron est déterministe à partir des dimensions cibles
      // (résolue côté OTS) : la clé dimensionnelle suffit à invalider les
      // meshes quand le placement change. On y ajoute la tuile texture (mm)
      // pour régénérer les UV quand la matière image change de textureSize.
      const cacheKey = getCacheKey(part, segDims, pattern, refSizes, textureTileMm);
      return { part, segDims, cacheKey };
    });
  }, [productConfig, mappedDimensions, modelFormData.pattern, refSizes, resolvedMaterial]);

  const selectionDebug = useMemo(() => {
    if (!isProductSelectionDebugAllowed()) return null;
    const { backendX, backendY, backendZ } = mappedDimensions;
    const base = buildProductSelectionDebug(
      products,
      backendX,
      backendY,
      backendZ,
      displayOptions,
    );
    return {
      ...base,
      rendererDimensions: {
        sizeX: modelFormData.sizeX,
        frontHeight: modelFormData.frontHeight,
        sizeZ: modelFormData.sizeZ,
        sizeL: modelFormData.sizeL,
        sizeH: modelFormData.sizeH,
        type: modelFormData.type,
        wallmount: modelFormData.wallmount,
        bottom: modelFormData.bottom,
        rear: modelFormData.rear,
      },
      productConfig: productConfig
        ? {
            id: productConfig.id,
            name: productConfig.name,
            partsTotal: productConfig.parts?.length ?? 0,
            partsIn3d: partsWithSegDims.length,
            pattern: modelFormData.pattern,
          }
        : null,
      priceFormula: productConfig?.priceFormula?.trim() || null,
    };
  }, [
    products,
    mappedDimensions,
    displayOptions,
    productConfig,
    partsWithSegDims,
    modelFormData,
  ]);

  useEffect(() => {
    if (isProductSelectionDebugAllowed()) {
      onSelectionDebugChange?.(selectionDebug);
    }
  }, [selectionDebug, onSelectionDebugChange]);

  // Vérifier si au moins une part a un ancrage - COMME LE BACKOFFICE
  // ⚠️ L'API backend retourne `part.anchor` (objet singulier), PAS `part.anchors` (array)
  const hasAnchors = useMemo(() => {
    return partsWithSegDims.some(({ part }) => !!part.anchor);
  }, [partsWithSegDims]);

  const ready = !!resolvedMaterial && partsWithSegDims.length > 0;

  // 3b. Cas "aucun produit ne correspond" : on committe un snapshot
  // "placeholders seuls" UNE FOIS la sélection résolue. On recalcule la
  // sélection ici (pur, peu coûteux) plutôt que de se baser sur l'état
  // `selectedProduct` afin d'éviter une bascule transitoire pas-de-produit →
  // produit au premier rendu. Sans ce commit, un changement de dimensions vers
  // une zone sans produit laisserait le spinner tourner indéfiniment.
  useEffect(() => {
    if (hide3D || products.length === 0) return;
    const { backendX, backendY, backendZ } = mappedDimensions;
    const selected = selectProductLocally(products, backendX, backendY, backendZ, displayOptions);
    if (selected) return; // un produit correspond → géré par le pipeline de chargement

    const { X, Y, Z } = mappedDimensions;
    setMeshes([]);
    setPlaceholderSize({ x: Z, y: X, z: Y });
    setMeshGeneration(prev => prev + 1);
    setCommittedSelectionKey(selectionKey);
    setCommittedInstallDims(installDims);
    setCommittedProduct({ parts: [], hasProduct: false });
    setIsLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, mappedDimensions, displayOptions, hide3D]);

  // 4. Charger les meshes des parts - LOGIQUE IDENTIQUE AU BACKOFFICE
  useEffect(() => {
    if (!ready) {
      return;
    }

    if (hide3D) return;

    // Éviter les chargements multiples simultanés
    if (loadingRef.current) return;

    const { X, Y, Z } = mappedDimensions;
    const pattern = modelFormData.pattern;
    const textureTileMm = getMaterialTextureTileMm(resolvedMaterial);

    // Signature des cacheKeys par part (dimensions + motif + tuile texture).
    const cacheSignature = partsWithSegDims
      .map(p => `${p.part.name}::${p.cacheKey}`)
      .join('|');

    // Vérifier si les dimensions / offsets ont changé
    const prevDims = prevDimensionsRef.current;
    const dimsChanged = !prevDims ||
      prevDims.X !== X ||
      prevDims.Y !== Y ||
      prevDims.Z !== Z ||
      prevDims.pattern !== pattern ||
      prevDims.productId !== productConfig?.id ||
      prevDims.cacheSignature !== cacheSignature ||
      prevDims.selectionKey !== selectionKey;

    if (!dimsChanged) return;

    if (loadingRef.current) {
      loadingRef.current = false;
    }

    // NOTE: On met à jour prevDimensionsRef APRÈS le chargement réussi, pas ici
    // Cela évite que des chargements annulés empêchent les rechargements suivants
    loadingRef.current = true;
    setIsLoading(true);
    
    
    let cancelled = false;

    (async () => {
      const cache = meshCacheRef.current;
      // PASS 1: Charger les meshes séquentiellement (1 requête OTS à la fois).
      // Évite de saturer l'event loop Node (~45s d'extrusion 3D × 6 en parallèle)
      // et laisse pattern-patron-placements / le backend répondre entre deux parts.
      const loadedMeshes = []; // { part, scene, cacheKey }
      const failedParts = [];

      for (const { part, segDims, cacheKey } of partsWithSegDims) {
        if (cancelled) break;

        if (cache.has(cacheKey)) {
          const scene = cache.get(cacheKey).clone();
          loadedMeshes.push({ part, scene, cacheKey });
          continue;
        }

        const gltf = await fetchPartGltf(
          productConfig.id,
          part,
          segDims,
          pattern,
          textureTileMm ?? undefined,
        );
        if (!gltf) {
          failedParts.push(part.name);
          continue;
        }

        const scene = gltf.scene;
        scene.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        // Surligne uniquement les arêtes EXTERNES (contour) de chaque part.
        addMeshEdgeLines(scene);

        const box = computeBBoxExcludingCutoutFlaps(scene);
        const meshSize = box.getSize(new THREE.Vector3());
        const bboxCenter = new THREE.Vector3();
        box.getCenter(bboxCenter);

        scene.userData.realSize = {
          width: meshSize.x,
          height: meshSize.y,
          depth: meshSize.z,
          bboxCenter: { x: bboxCenter.x, y: bboxCenter.y, z: bboxCenter.z },
        };

        cache.set(cacheKey, scene.clone());
        loadedMeshes.push({ part, scene, cacheKey });
      }

      if (failedParts.length > 0) {
        console.warn(`[FacesLoaderDynamic] ${failedParts.length} part(s) failed: ${failedParts.join(', ')}`);
      }

      if (cancelled) return;

      // ============================================================
      // PASS 2: Calculer les positions d'ancrage et créer les groupes
      // ============================================================
      const parts = partsWithSegDims.map(p => p.part);
      let anchoredPositions = null;

      const realMeshSizes = {};
      for (const { part, scene } of loadedMeshes) {
        const partId = String(part.clientId || part.id);
        if (scene.userData.realSize) {
          realMeshSizes[partId] = scene.userData.realSize;
        }
      }
      
      if (hasAnchors) {
        const { backendX, backendY, backendZ } = mappedDimensions;
        const productSize = { x: backendX, y: backendY, z: backendZ };
        anchoredPositions = resolvePositions(parts, productSize, realMeshSizes);
      }
      
      // Créer les groupes finaux avec positions
      const newMeshes = [];
      const { backendX, backendY, backendZ } = mappedDimensions;
      const productSizeForSeams = { x: backendX, y: backendY, z: backendZ };

      for (const { part, scene } of loadedMeshes) {
        const partId = String(part.clientId || part.id);
        
        // Créer le groupe parent
        const group = new THREE.Group();
        group.name = `group_${part.name}`;
        
        scene.rotation.set(0, 0, 0);
        group.add(scene);

        const partDims = getPartDims(part, realMeshSizes, productSizeForSeams);
        const seamLine = createPartSeamLine(part, partDims);
        if (seamLine) group.add(seamLine);
        
        // Appliquer la rotation au groupe
        const rotX = (part.rotationX ?? 0) * Math.PI / 180;
        const rotY = (part.rotationY ?? 0) * Math.PI / 180;
        const rotZ = (part.rotationZ ?? 0) * Math.PI / 180;
        group.rotation.set(rotX, rotY, rotZ);
        
        // Appliquer la position
        if (anchoredPositions && anchoredPositions[partId]) {
          const pos = anchoredPositions[partId];
          group.position.set(pos.x, pos.y, pos.z);
        } else {
          // Fallback : utiliser posX/posY/posZ du positionnement 3D
          const px = part.posX ?? 0;
          const py = part.posY ?? 0;
          const pz = part.posZ ?? 0;
          group.position.set(px, py, pz);
        }
        
        newMeshes.push({ 
          mesh: group, 
          partInfo: { 
            name: part.name, 
            posX: part.posX, posY: part.posY, posZ: part.posZ,
            resizeAxisX: part.resizeAxisX, resizeAxisY: part.resizeAxisY,
          } 
        });
      }

      if (!cancelled) {

        // C.1 - Préserver les meshes existants en cas de fail. On fusionne
        // `newMeshes` (les parts fraichement chargées) avec l'état précédent :
        // les parts en échec transitoire qui avaient un mesh affiché
        // conservent leur ancien mesh (= pas de disparition visuelle), et
        // une part qui n'a JAMAIS chargé reste absente (= comportement
        // précédent inchangé pour les nouveaux échecs).
        const failedNamesSet = new Set(failedParts);
        setMeshes(prevMeshes => {
          const prevByPartName = new Map(prevMeshes.map(m => [m.partInfo?.name, m]));
          const freshByPartName = new Map(newMeshes.map(m => [m.partInfo?.name, m]));
          const result = [];
          for (const { part } of partsWithSegDims) {
            const fresh = freshByPartName.get(part.name);
            if (fresh) {
              result.push(fresh);
              continue;
            }
            if (failedNamesSet.has(part.name) && prevByPartName.has(part.name)) {
              // Part en échec transitoire : on garde l'ancien mesh affiché.
              console.warn(`[FacesLoaderDynamic] "${part.name}" en échec transitoire, conservation du mesh précédent.`);
              result.push(prevByPartName.get(part.name));
            }
            // Sinon : pas d'ancien mesh + pas de fresh → on n'ajoute rien.
          }
          return result;
        });

        // C.3 - Ne mettre à jour prevDimensionsRef QUE si TOUT a chargé.
        // Sinon, le prochain trigger (même sans changement de dimensions)
        // pourra retenter les parts en échec.
        if (failedParts.length === 0) {
          prevDimensionsRef.current = {
            X,
            Y,
            Z,
            pattern,
            productId: productConfig?.id,
            cacheSignature,
            selectionKey,
          };
        } else {
          console.warn(`[FacesLoaderDynamic] prevDimensionsRef NON mis à jour (${failedParts.length} fail) → retry possible au prochain trigger.`);
        }

        // C.2 - Si des parts sont toujours en échec après les retries
        // inline, on déclenche un retry "effect-level" différé.
        if (failedParts.length > 0 && !pendingRetryRef.current) {
          console.warn(`[FacesLoaderDynamic] Programmation d'un retry effect-level dans ${OTS_EFFECT_RETRY_DELAY_MS}ms pour ${failedParts.length} part(s).`);
          pendingRetryRef.current = setTimeout(() => {
            pendingRetryRef.current = null;
            setRetryTick(t => t + 1);
          }, OTS_EFFECT_RETRY_DELAY_MS);
        }

        setMeshGeneration(prev => prev + 1); // Force re-render des primitives
        setPlaceholderSize({ x: Z, y: X, z: Y });
        // Publication ATOMIQUE du snapshot produit : meshes, placeholderSize,
        // meshGeneration, committedInstallDims et committedProduct sont posés
        // dans le même flush React. Le rendu (meshes ET placeholders) bascule
        // donc vers la nouvelle config d'un seul coup.
        setCommittedSelectionKey(selectionKey);
        setCommittedInstallDims(installDims);
        setCommittedProduct({ parts: productConfig?.parts || [], hasProduct: true });
        setIsLoading(false);
        loadingRef.current = false;
        
        // 🆕 Calculer la bounding box globale de tous les meshes positionnés
        if (onAllFacesLoaded) {
          const globalBox = new THREE.Box3();
          newMeshes.forEach(({ mesh }) => {
            globalBox.expandByObject(mesh);
          });
          const globalCenter = new THREE.Vector3();
          const globalSize = new THREE.Vector3();
          globalBox.getCenter(globalCenter);
          globalBox.getSize(globalSize);
          
          // 🆕 Appliquer le même facteur d'échelle que le groupe de rendu (FACE_SCALE = 0.1)
          const scaledCenter = {
            x: globalCenter.x * FACE_SCALE,
            y: globalCenter.y * FACE_SCALE,
            z: globalCenter.z * FACE_SCALE,
          };
          const scaledSize = {
            x: globalSize.x * FACE_SCALE,
            y: globalSize.y * FACE_SCALE,
            z: globalSize.z * FACE_SCALE,
          };
          

          onAllFacesLoaded({
            center: scaledCenter,
            size: scaledSize,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      loadingRef.current = false; // Permettre un nouveau chargement après cleanup
      // Annuler tout retry effect-level en attente — il sera re-programmé
      // au cycle suivant si nécessaire.
      if (pendingRetryRef.current) {
        clearTimeout(pendingRetryRef.current);
        pendingRetryRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partsWithSegDims, resolvedMaterial, modelFormData.pattern, hasAnchors, productConfig, mappedDimensions, hide3D, retryTick, selectionKey]);

  // 4b. Synchronisation "à vide" du snapshot installDims des placeholders.
  // Garde FIABLE basée sur les valeurs : si le selectionKey courant diffère du
  // committé, un rechargement (produit/dimensions/type/motif) est en cours → on
  // gèle les installDims jusqu'au commit (qui publiera installDims EN MÊME TEMPS
  // que les meshes, cf. effet 4). S'ils sont égaux, aucun reload n'est requis
  // (ex. cache verrouillé : L/H/P1/P2 changent sans toucher aux dimensions du
  // cache) → on rafraîchit les installDims en direct.
  // ⚠️ Cette comparaison de valeurs est insensible à l'ordre des effets et à
  // hide3D, contrairement à un flag de "reload en cours" qui fuyait sur l'étape
  // de sélection du type d'installation.
  useEffect(() => {
    // Un rechargement (produit/dimensions/type/motif) est en cours → on gèle.
    if (selectionKey !== committedSelectionKey) return;
    // Sécurité supplémentaire : ne jamais publier pendant un chargement actif.
    if (loadingRef.current) return;
    setCommittedInstallDims(installDims);
  }, [installDims, selectionKey, committedSelectionKey]);

  // 5. Appliquer le matériau aux meshes - COMME LE BACKOFFICE
  // 🆕 Rotation UV par part (textureRotation en degrés).
  //
  // ⚠️ `getMaterialFromFormData` (renderer) MET EN CACHE le matériau et sa
  // texture (partagés entre toutes les parts). On ne peut donc PAS muter
  // directement `mat.map.rotation` : la rotation d'une part fuiterait sur les
  // autres, et une part à 0° hériterait de la rotation d'une part à 90°.
  // On construit donc un variant de matériau PAR valeur de rotation (clone du
  // matériau de base + clone de ses textures, qui partagent l'image source
  // donc pas de re-téléchargement). La rotation est appliquée explicitement,
  // y compris 0° (sinon pollution du cache partagé).
  useEffect(() => {
    if (!resolvedMaterial || meshes.length === 0) return;
    // On utilise les parts du snapshot publié (appariées aux meshes affichés),
    // pas productConfig.parts qui peut déjà refléter un produit en cours de
    // chargement (meshes pas encore prêts).
    const allParts = committedProduct?.parts || productConfig?.parts || [];

    const materialKey = JSON.stringify(resolvedMaterial);
    if (baseMatRef.current.key !== materialKey) {
      baseMatRef.current = {
        key: materialKey,
        mat: getMaterialFromFormData(resolvedMaterial, () => {
          setMaterialRevision((v) => v + 1);
        }),
      };
    }

    const baseMat = baseMatRef.current.mat;
    const matByRotation = new Map();

    const applyRotationToMap = (map, rotationRad) => {
      applyMapRotation(map, rotationRad);
    };

    const getRotatedMaterial = (rotationRad) => {
      if (matByRotation.has(rotationRad)) return matByRotation.get(rotationRad);

      const m = baseMat.clone();
      if (baseMat.map) {
        m.map = baseMat.map.clone();
        applyRotationToMap(m.map, rotationRad);
      }
      if (baseMat.normalMap) {
        m.normalMap = baseMat.normalMap.clone();
        applyRotationToMap(m.normalMap, rotationRad);
      }
      syncNormalMapFromColorMap(m);
      m.needsUpdate = true;
      matByRotation.set(rotationRad, m);
      return m;
    };

    meshes.forEach(({ mesh, partInfo }) => {
      const part = allParts.find((p) => p.name === partInfo?.name);
      const rotationDeg = typeof part?.textureRotation === 'number' ? part.textureRotation : 0;
      const rotationRad = (rotationDeg * Math.PI) / 180;
      const mat = getRotatedMaterial(rotationRad);
      mesh.traverse((child) => {
        if (child.isMesh && !child.userData?.isEdgeOverlay) {
          child.material = mat;
          child.material.needsUpdate = true;
        }
      });
    });
  }, [resolvedMaterial, meshes, committedProduct, productConfig, materialRevision]);

  // Ne rien afficher tant que le matériau n'est pas prêt…
  if (!resolvedMaterial) return null;
  // …ni tant qu'aucun snapshot n'a été publié (premier chargement). Pendant ce
  // temps, ThreeScene affiche le spinner et grise le rendu : on n'affiche donc
  // jamais une scène partiellement chargée.
  if (!committedProduct) return null;

  const { parts: committedParts, hasProduct } = committedProduct;
  // Dimensions d'installation du snapshot publié (fallback sur la valeur live au
  // tout premier rendu, avant le premier commit). Les placeholders n'utilisent
  // QUE cette valeur figée : ils changent donc en même temps que les meshes.
  const placeholderInstallDims = committedInstallDims || installDims;

  // Rendu unifié, piloté UNIQUEMENT par le snapshot publié :
  //   - meshes produit affichés seulement si hasProduct (snapshot figé)
  //   - placeholders ancrés sur les meshes/parts du MÊME snapshot
  // Aucune valeur "en cours de chargement" (selectedProduct, productConfig)
  // n'intervient ici, ce qui élimine les états transitoires incohérents.
  return (
    <group scale={[FACE_SCALE, FACE_SCALE, FACE_SCALE]}>
      {hasProduct && meshes.map(({ mesh }, i) => (
        <group key={`${meshGeneration}-${i}-${mesh.uuid}`}>
          <primitive object={mesh} />
        </group>
      ))}

      {placeholderSize && effectivePlaceholders
        .filter(ph => ph.enabled !== false)
        .filter(ph => evaluateDisplayFormula(
          ph.displayFormula,
          placeholderSize.x, placeholderSize.y, placeholderSize.z,
          placeholderInstallDims,
        ))
        .map((ph, index) => (
          <PlaceholderCube 
            key={ph.id || `placeholder-${index}`}
            name={ph.name || `Placeholder #${index}`}
            size={placeholderSize} 
            hide3D={hasProduct ? hide3D : false} 
            globalSceneSettings={globalSceneSettings}
            placeholderFormulaX={ph.formulaX}
            placeholderFormulaY={ph.formulaY}
            placeholderFormulaZ={ph.formulaZ}
            placeholderColor={ph.color}
            placeholderEmissive={ph.emissive}
            placeholderEmissiveIntensity={ph.emissiveIntensity}
            anchorPartId={ph.anchorPartId}
            anchorSide={ph.anchorSide || 'inside'}
            offsetX={ph.offsetX ?? 0}
            offsetY={ph.offsetY ?? 0}
            offsetZ={ph.offsetZ ?? 0}
            meshes={meshes}
            parts={committedParts}
            installDims={placeholderInstallDims}
          />
        ))
      }
    </group>
  );
};

export default FacesLoaderDynamic;
